import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { ShieldCheck, Loader2 } from "lucide-react";

const ACTION_STYLE = {
  create: "bg-green-50 text-green-600",
  update: "bg-blue-50 text-blue-600",
  delete: "bg-red-50 text-red-600",
};

export default function AuditLog() {
  const [logs, setLogs] = useState(null);

  useEffect(() => {
    api.get("/audit-logs?limit=200").then(({ data }) => setLogs(data)).catch(() => setLogs([]));
  }, []);

  return (
    <Layout>
      <div className="animate-fade-in-up space-y-6">
        <div>
          <p className="text-sm text-slate-400 font-medium">Compliance · who did what, when</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">Audit Log</h1>
        </div>

        {logs === null ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-indigo-600 animate-spin" /></div>
        ) : logs.length === 0 ? (
          <div className="bg-card rounded-2xl border border-slate-100 p-12 text-center">
            <ShieldCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No audit entries yet. Actions like creating clients and users are recorded here.</p>
          </div>
        ) : (
          <div className="bg-card rounded-2xl border border-slate-100 shadow-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/50">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Entity</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Actor</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Details</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">When</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} data-testid="audit-row" className="border-t border-slate-50 hover:bg-indigo-50/30 transition-colors">
                      <td className="px-5 py-3.5">
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-md ${ACTION_STYLE[l.action] || "bg-slate-100 text-slate-500"}`}>{l.action}</span>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-700 capitalize">{l.entity_type}</td>
                      <td className="px-5 py-3.5">
                        <p className="text-sm text-slate-700">{l.actor_email}</p>
                        <p className="text-[11px] text-slate-400">{l.actor_role}</p>
                      </td>
                      <td className="px-5 py-3.5">
                        <code className="text-[11px] font-mono text-slate-500 bg-slate-50 px-2 py-1 rounded-md">
                          {Object.entries(l.details || {}).map(([k, v]) => `${k}: ${v}`).join(", ") || "—"}
                        </code>
                      </td>
                      <td className="px-5 py-3.5 text-xs text-slate-400 whitespace-nowrap">{l.timestamp ? new Date(l.timestamp).toLocaleString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
